"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { DateRangePicker, RangeKeyDict, Range } from 'react-date-range';
import { addDays, differenceInDays, format } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';

const NIGHTLY_RATE = 405;
const CLEANING_FEE = 100;
const PET_FEE = 75;
const EXTRA_GUEST_FEE = 50;
const BASE_GUEST_COUNT = 12;

export default function SimpleBookingCard() {
  const router = useRouter();
  const [dateRange, setDateRange] = useState<Range[]>([
    {
      startDate: new Date(),
      endDate: addDays(new Date(), 2),
      key: 'selection'
    }
  ]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [guests, setGuests] = useState(2);
  const [pets, setPets] = useState(0);
  const [isSticky, setIsSticky] = useState(false);

  // Calculate nights
  const nights = differenceInDays(
    dateRange[0].endDate || new Date(),
    dateRange[0].startDate || new Date()
  );

  // Calculate costs
  const nightsCost = nights * NIGHTLY_RATE;
  const extraGuests = Math.max(0, guests - BASE_GUEST_COUNT);
  const extraGuestsCost = extraGuests * EXTRA_GUEST_FEE;
  const petsCost = pets * PET_FEE;
  const totalCost = nightsCost + extraGuestsCost + petsCost + CLEANING_FEE;

  // Sticky scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 200);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Quick date presets
  const applyPreset = (preset: 'weekend' | 'nextWeek' | 'longWeekend') => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ..., 5=Fri, 6=Sat
    let start: Date;
    let end: Date;

    switch (preset) {
      case 'weekend':
        // Next Friday to Sunday
        let daysToFriday = 5 - dayOfWeek;
        if (daysToFriday <= 0) daysToFriday += 7;
        start = addDays(today, daysToFriday);
        end = addDays(start, 2);
        break;
      case 'longWeekend':
        // Next Friday to Monday
        let daysToFri = 5 - dayOfWeek;
        if (daysToFri <= 0) daysToFri += 7;
        start = addDays(today, daysToFri);
        end = addDays(start, 3);
        break;
      case 'nextWeek':
        start = addDays(today, 7);
        end = addDays(start, 7);
        break;
    }

    setDateRange([{ startDate: start, endDate: end, key: 'selection' }]);
    setShowCalendar(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams({
      start: format(dateRange[0].startDate || new Date(), 'yyyy-MM-dd'),
      end: format(dateRange[0].endDate || new Date(), 'yyyy-MM-dd'),
      guests: guests.toString(),
      pets: pets.toString(),
    });
    router.push(`/book?${params.toString()}`);
  };

  return (
    <div className={`transition-all duration-300 ${isSticky ? 'shadow-2xl' : 'shadow-lg'}`}>
      <form
        className="bg-green-950 bg-opacity-80 rounded-xl p-4 md:p-6 border-2 border-green-800"
        onSubmit={handleSubmit}
      >
        {/* Header with Price */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b-2 border-green-700">
          <div>
            <p className="text-3xl md:text-4xl font-bold">${NIGHTLY_RATE}</p>
            <p className="text-sm text-gray-300">per night</p>
          </div>
          {nights > 0 && (
            <div className="text-right">
              <p className="text-2xl font-bold text-green-300">${totalCost}</p>
              <p className="text-xs text-gray-400">{nights} {nights === 1 ? 'night' : 'nights'}</p>
            </div>
          )}
        </div>

        {/* Date Selection */}
        <div className="mb-4">
          <label className="text-sm font-semibold mb-2 block">Check-in → Check-out</label>
          <button
            type="button"
            onClick={() => setShowCalendar(!showCalendar)}
            className="w-full px-4 py-3 bg-green-900 rounded-lg text-left hover:bg-green-800 transition-colors"
          >
            <div className="flex items-center justify-between">
              <span>
                {format(dateRange[0].startDate || new Date(), 'MMM dd, yyyy')} → {format(dateRange[0].endDate || new Date(), 'MMM dd, yyyy')}
              </span>
              <span>{showCalendar ? '▲' : '▼'}</span>
            </div>
          </button>

          {/* Calendar Popover */}
          {showCalendar && (
            <div className="absolute z-50 mt-2 bg-green-900 rounded-xl shadow-2xl border-2 border-green-700 p-4 md:p-6 left-0 right-0 md:left-auto md:right-auto max-w-full overflow-hidden">
              {/* Quick Presets */}
              <div className="mb-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyPreset('weekend')}
                  className="px-3 py-1.5 text-sm bg-green-700 hover:bg-green-600 rounded-full transition-colors font-medium"
                >
                  This Weekend
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('longWeekend')}
                  className="px-3 py-1.5 text-sm bg-green-700 hover:bg-green-600 rounded-full transition-colors font-medium"
                >
                  Long Weekend
                </button>
                <button
                  type="button"
                  onClick={() => applyPreset('nextWeek')}
                  className="px-3 py-1.5 text-sm bg-green-700 hover:bg-green-600 rounded-full transition-colors font-medium"
                >
                  Next Week
                </button>
              </div>

              {/* Date Range Picker */}
              <div className="booking-calendar-wrapper">
                <DateRangePicker
                  ranges={dateRange}
                  onChange={(item: RangeKeyDict) => setDateRange([item.selection])}
                  minDate={new Date()}
                  rangeColors={['#15803d']}
                  className="booking-calendar"
                  months={1}
                  direction="horizontal"
                  showMonthAndYearPickers={true}
                  showDateDisplay={false}
                />
              </div>

              <button
                type="button"
                onClick={() => setShowCalendar(false)}
                className="w-full mt-4 px-4 py-2.5 bg-green-700 hover:bg-green-600 rounded-lg transition-colors font-semibold"
              >
                Done
              </button>
            </div>
          )}
        </div>

        {/* Guests and Pets */}
        <div className="mb-4 pb-4 border-b-2 border-green-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Guests */}
            <div>
              <label className="text-sm font-semibold mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
                Guests
              </label>
              <div className="flex items-center justify-between bg-green-900 rounded-lg p-2">
                <button
                  type="button"
                  onClick={() => setGuests(Math.max(1, guests - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-green-700 hover:bg-green-600 rounded-lg text-xl font-bold"
                >
                  −
                </button>
                <span className="text-lg font-semibold">{guests}</span>
                <button
                  type="button"
                  onClick={() => setGuests(Math.min(14, guests + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-green-700 hover:bg-green-600 rounded-lg text-xl font-bold"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Up to 14 guests</p>
            </div>

            {/* Pets */}
            <div>
              <label className="text-sm font-semibold mb-2 flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                </svg>
                Pets
              </label>
              <div className="flex items-center justify-between bg-green-900 rounded-lg p-2">
                <button
                  type="button"
                  onClick={() => setPets(Math.max(0, pets - 1))}
                  className="w-10 h-10 flex items-center justify-center bg-green-700 hover:bg-green-600 rounded-lg text-xl font-bold"
                >
                  −
                </button>
                <span className="text-lg font-semibold">{pets}</span>
                <button
                  type="button"
                  onClick={() => setPets(Math.min(3, pets + 1))}
                  className="w-10 h-10 flex items-center justify-center bg-green-700 hover:bg-green-600 rounded-lg text-xl font-bold"
                >
                  +
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Up to 3 pets ($75 each)</p>
            </div>
          </div>
        </div>

        {/* Price Breakdown */}
        {nights > 0 && (
          <div className="mb-4 pb-4 border-b-2 border-green-700 space-y-2">
            <div className="flex justify-between text-sm">
              <span>{nights} night{nights !== 1 ? 's' : ''} × ${NIGHTLY_RATE}</span>
              <span className="font-semibold">${nightsCost}</span>
            </div>
            {extraGuests > 0 && (
              <div className="flex justify-between text-sm">
                <span>{extraGuests} extra guest{extraGuests !== 1 ? 's' : ''} × ${EXTRA_GUEST_FEE}</span>
                <span className="font-semibold">${extraGuestsCost}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span>Cleaning fee</span>
              <span className="font-semibold">${CLEANING_FEE}</span>
            </div>
            {pets > 0 && (
              <div className="flex justify-between text-sm">
                <span>{pets} pet{pets !== 1 ? 's' : ''} × ${PET_FEE}</span>
                <span className="font-semibold">${petsCost}</span>
              </div>
            )}
            <div className="flex justify-between text-lg font-bold pt-2 border-t border-green-700">
              <span>Total</span>
              <span className="text-green-300">${totalCost}</span>
            </div>
          </div>
        )}

        {/* Trust Indicators */}
        <div className="mb-4 flex flex-wrap gap-2 text-xs">
          <span className="px-2 py-1 bg-green-700 bg-opacity-50 rounded-full">✓ Free cancellation</span>
          <span className="px-2 py-1 bg-green-700 bg-opacity-50 rounded-full">⚡ Instant booking</span>
          <span className="px-2 py-1 bg-green-700 bg-opacity-50 rounded-full">★ 4.8 avg rating</span>
        </div>

        {/* CTA Button */}
        <button
          type="submit"
          className="w-full py-3 md:py-4 rounded-lg font-bold text-lg bg-green-600 hover:bg-green-500 text-white shadow-lg hover:shadow-xl transition-all"
        >
          Reserve for ${totalCost}
        </button>

        <p className="text-xs text-gray-400 text-center mt-3">
          You won&apos;t be charged yet
        </p>
      </form>
    </div>
  );
}
